import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  NormalizeSlugs,
  sortProductsByMsrpAndTitle,
} from '../../../../../Utils/helper-functions';

import {
  useProductsHook,
  useRoutingHook,
  useRetailerHook,
  useLanguageHook,
} from '../../../../../Hooks/use-context-hooks';

import { filterBrowsableProducts } from '../../../../../Utils/product-availability';

import styles from './DetailsPage.module.css';
import { DesktopProductDetailsPage } from '../Deskop/DesktopProductDetails';
import { MobileProductDetailsPage } from '../Mobile/MobileProductDetails';
import NotFoundPage from '../../../NotFoundPage/NotFoundPage';

import { ProductPageNavCard } from '../../../../../components/ProductCards/ProductPageNavCard/ProductPageNavCard';
import { Link } from 'react-router-dom';
import { ProductDetailsPageSkeletonComponent } from '../ProductDetailsSkeleton';

const DetailsPage = () => {
  const { productId, categoryId } = useParams();

  const { publicProducts } = useProductsHook();
  const { translatedText, language } = useLanguageHook();
  const { productNavigationState, setProductNavigationState } = useRoutingHook();
  const { isHomeDepotApp } = useRetailerHook();

  const [product, setProduct] = useState(null);
  const [subcategoryProducts, setSubcategoryProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const foundProduct = publicProducts.find(
      prod =>
        NormalizeSlugs(prod.categoryEN) === categoryId &&
        prod.title === productId
    );

    if (foundProduct) {
      setProduct(foundProduct);

      const subcategoryKey =
        language === 'FR'
          ? foundProduct.subcategoryFR
          : foundProduct.subcategoryEN;

      const subcategorySlug = NormalizeSlugs(subcategoryKey);

      const cachedProductsList =
        productNavigationState?.subcategoryProductLists?.[subcategorySlug];

      const hasCachedProductsList = Array.isArray(cachedProductsList);

      let productsList = hasCachedProductsList
        ? filterBrowsableProducts(cachedProductsList)
        : null;

      if (!hasCachedProductsList) {
        const productsForSubcat = publicProducts.filter(prod =>
          NormalizeSlugs(language === 'FR' ? prod.subcategoryFR : prod.subcategoryEN) === subcategorySlug &&
          NormalizeSlugs(prod.categoryEN) === categoryId
        );

        const browsableProductsForSubcat = filterBrowsableProducts(productsForSubcat);

        const sortedMap = sortProductsByMsrpAndTitle({
          [subcategoryKey]: browsableProductsForSubcat,
        });

        productsList = sortedMap[subcategoryKey] || [];

        setProductNavigationState(prevState => ({
          ...prevState,
          subcategoryProductLists: {
            ...(prevState.subcategoryProductLists || {}),
            [subcategorySlug]: productsList,
          },
        }));
      }

      setSubcategoryProducts(productsList || []);
      setIsLoading(false);
    } else {
      setProduct(null);
      setSubcategoryProducts([]);

      const timer = setTimeout(() => setIsLoading(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [
    publicProducts,
    categoryId,
    productId,
    productNavigationState,
    setProductNavigationState,
    language,
  ]);

  const productNavCardText = {
    forward: translatedText.productNavCardUp,
    back: translatedText.productNavCardDown,
  };

  if (isLoading) {
    return <ProductDetailsPageSkeletonComponent />;
  }

  if (product) {
    const currentIndex = subcategoryProducts.findIndex(
      prod => prod.title === product.title
    );

    const currentProductIsInNav = currentIndex >= 0;

    const prevProduct = currentProductIsInNav
      ? subcategoryProducts[currentIndex - 1] || null
      : null;

    const nextProduct = currentProductIsInNav
      ? subcategoryProducts[currentIndex + 1] || null
      : null;

    const basePath = isHomeDepotApp?.isHomeDepotActive
      ? '/home-depot'
      : '';

    const nextNavLink = nextProduct
      ? `${basePath}/appliances/${NormalizeSlugs(nextProduct.categoryEN)}/${nextProduct.title}`
      : null;

    const prevNavLink = prevProduct
      ? `${basePath}/appliances/${NormalizeSlugs(prevProduct.categoryEN)}/${prevProduct.title}`
      : null;

    return (
      <>
        <div className={styles.mobileAppliancePageContainer}>
          <div className={styles.navLinksContainer}>
            <div className={styles.navigationLinks}>
              {prevProduct && prevNavLink && (
                <Link to={prevNavLink}>
                  <button
                    className={styles.directionalButton}
                    type="button"
                  >
                    ← {prevProduct.title} {productNavCardText.forward}
                  </button>
                </Link>
              )}

              {nextProduct && nextNavLink && (
                <Link to={nextNavLink}>
                  <button
                    type="button"
                    className={styles.directionalButton}
                  >
                    {productNavCardText.back} {nextProduct.title} →
                  </button>
                </Link>
              )}
            </div>
          </div>

          <MobileProductDetailsPage productId={productId} />
        </div>

        <div className={styles.desktopAppliancePageContainer}>
          <div className={styles.productPageNavContainer}>
            <div className={styles.productPageInnerNavContainer}>
              <div className={styles.productPageNavWrapper}>
                {prevProduct && prevNavLink && (
                  <ProductPageNavCard
                    product={prevProduct}
                    href={prevNavLink}
                    stylesProp="one"
                    gridConfig={{
                      gridTemplateColumns: '1fr, .5fr',
                      textCol: 1,
                      imgCol: 2,
                      textAlign: 'right',
                      alignSelf: 'flex-end',
                      productNavCardPadding: '1rem 0 2rem 2rem',
                      productNavCardMargin: '0 1rem 0 0',
                      productNavCardBorderBottomRightRadius: '0',
                      productNavCardBorderBottomLeftRadius: '2rem',
                      flexDirection: 'column',
                      navCardItem1Padding: '0rem',
                    }}
                    productDirectionConfig={
                      <button
                        className={styles.directionalButton}
                        type="button"
                      >
                        ← {productNavCardText.forward}
                      </button>
                    }
                  />
                )}

                {nextProduct && nextNavLink && (
                  <ProductPageNavCard
                    product={nextProduct}
                    href={nextNavLink}
                    stylesProp="two"
                    gridConfig={{
                      gridTemplateColumns: '.5fr, 1fr',
                      textCol: 2,
                      imgCol: 1,
                      productNavCardPadding: '1rem 2rem 2rem 0',
                      productNavCardMargin: '0 0 0 1rem',
                      productNavCardBorderBottomRightRadius: '2rem',
                      productNavCardBorderBottomLeftRadius: '0',
                      flexDirection: 'column',
                      navCardItem1Padding: '0rem',
                    }}
                    productDirectionConfig={
                      <button
                        className={styles.directionalButton}
                        type="button"
                      >
                        {productNavCardText.back} →
                      </button>
                    }
                  />
                )}
              </div>
            </div>
          </div>

          <DesktopProductDetailsPage productId={productId} />
        </div>
      </>
    );
  }

  return <NotFoundPage />;
};

export default DetailsPage;